<template>
  <div>
    <b-container class="py-3">
      <b-row v-if="form">
        <b-col>
          <span class="pull-right">
            <b-progress :max="form.pages.length" height="2rem" variant="success" class="text-center" striped>
              <b-progress-bar :value="pageIndex + 1">
                Page {{pageIndex + 1}} of {{form.pages.length}}
              </b-progress-bar>
            </b-progress>
          </span>
        </b-col>
      </b-row>

      <b-row v-if="form" class="mt-3">
        <b-col order="2" order-md="1" md="6">
          <page-title :title="page.template.title" />
        </b-col>
        <b-col order="1" order-md="2" md="6" class="text-right mt-3">
          <b-button-group>
            <b-button v-if="!isOfflineStopped" @click="save()">Save</b-button>
            <b-button v-if="pageIndex > 0" @click="save(getPageUrl(form.pages[pageIndex - 1]))">Back</b-button>
            <b-dropdown right text="Jump to Page">
              <b-dropdown-item v-for="jumpPage in form.pages" :key="jumpPage.id" @click="save(getPageUrl(jumpPage))" :disabled="jumpPage.displayWeight > maxDisplayWeight && !$route.query.allowJump">
                {{jumpPage.template.title}}
              </b-dropdown-item>
            </b-dropdown>
            <b-button v-if="(pageIndex < form.pages.length - 1) && !isOfflineStopped" @click="saveNext" variant="primary">Next</b-button>
            <b-button v-if="(pageIndex == form.pages.length - 1) && !isOfflineStopped" @click="saveNext" variant="primary">Finish</b-button>
          </b-button-group>
        </b-col>
      </b-row>

      <transition name="fade">
        <b-row v-if="answerSet">
          <b-col>

            <b-alert v-if="isOfflineStopped" show variant="info">
              You have completed the portions of this questionnaire that can be provided while offline. To complete this questionnaire, please
              reconnect to the Internet and check in this client.
            </b-alert>

            <b-form @submit.prevent v-if="!isOfflineStopped">
              <p class="text-muted">Select Save or Next before leaving this page. Changes are not saved automatically.</p>
              <div v-for="(question, index) in page.template.questions" v-bind:key="index">
                <div v-if="isVisible(question)">
                  <text-control :required="question.isRequired" v-if="question.questionType == 'Text'" v-model="answerSet.answers[getAnswerIndex(question.key)].value" :label="question.label" :top-description="question.description" />
                  <textarea-control :required="question.isRequired" v-if="question.questionType == 'Textarea'" v-model="answerSet.answers[getAnswerIndex(question.key)].value" :label="question.label" :top-description="question.description" />
                  <date-picker-control :required="question.isRequired" v-if="question.questionType == 'DatePicker'" v-model="answerSet.answers[getAnswerIndex(question.key)].value" :label="question.label" :top-description="question.description" />
                  <select-list-control :required="question.isRequired" v-if="question.questionType == 'SelectList'" v-model="answerSet.answers[getAnswerIndex(question.key)].value" :label="question.label" :top-description="question.description" :options="question.options" />
                  <radio-button-list-control :required="question.isRequired" v-if="question.questionType == 'RadioButtonList'" v-model="answerSet.answers[getAnswerIndex(question.key)].value" :label="question.label" :top-description="question.description" :options="question.options" :name="question.key" />
                  <check-box-list-control :required="question.isRequired" v-if="question.questionType == 'CheckBoxList'" v-model="answerSet.answers[getAnswerIndex(question.key)].value" :label="question.label" :top-description="question.description" :options="question.options" :name="question.key" delimiter="|" />
                  <file-upload-control :required="question.isRequired" v-if="question.questionType == 'File'" v-model="answerSet.answers[getAnswerIndex(question.key)].value" :label="question.label" :top-description="question.description" :options="question.options" :name="question.key" @upload="uploadFile(question.key, $event)" @download="downloadFile(question.key, $event)" />
                  <div v-if="question.questionType == 'Label'" v-html="renderMarkdown(question.label)">
                  </div>
                  <div v-if="question.key=='PermanentAddressLabel'" @click="copyPermanentAddressFields">
                    <b-link>Same as Current Address</b-link>
                  </div>
                </div>
              </div>

              <div class="mt-3">
                <b-button v-if="pageIndex < form.pages.length - 1" @click="saveNext" variant="primary" type="submit">Next</b-button>
                <b-button v-if="pageIndex == form.pages.length - 1" @click="saveNext" variant="primary">Finish</b-button>
              </div>

            </b-form>

          </b-col>
        </b-row>
      </transition>
    </b-container>

  </div>
</template>

<script>
import AuthMixin from '../Mixins/AuthMixin.vue';
import FormMixin from '../Mixins/FormMixin.vue';
import axios from "axios";
import marked from 'marked';
import Vue from 'vue';

export default {
  name: "ClientForm",
  mixins: [
    FormMixin
  ],
  props: [
    'formId',
    'pageId'
  ],
  data() {
    return {
      form: null,
      answerSet: null,
      maxDisplayWeight: 0,
      savedAnswers: null
    }
  },
  computed: {
    hasUnsavedAnswers: function () {
      return !!this.answerSet && this.savedAnswers !== null
        && JSON.stringify(this.answerSet.answers) !== this.savedAnswers;
    },
    page: function () {
      if (!this.form || !this.form.pages)
        return null;

      return this.form.pages.find(p => p.id == this.pageId);
    },
    pageIndex: function () {
      if (!this.form || !this.form.pages)
        return null;

      return this.form.pages.findIndex(p => p.id == this.pageId);
    },
    isOfflineStopped: function () {
      return this.form.client.isOffline && this.page.template.stopIfOffline;
    }
  },
  methods: {
    warnBeforeLeaving: function (event) {
      if (this.hasUnsavedAnswers) {
        event.preventDefault();
        event.returnValue = '';
      }
    },
    copyPermanentAddressFields: function () {
      this.copyFields([
        { srcKey: 'CurrentAddressLine1', dstKey: 'PermanentAddressLine1' },
        { srcKey: 'CurrentAddressLine2', dstKey: 'PermanentAddressLine2' },
        { srcKey: 'CurrentAddressCity', dstKey: 'PermanentAddressCity' },
        { srcKey: 'CurrentAddressState', dstKey: 'PermanentAddressState' },
        { srcKey: 'CurrentAddressZip', dstKey: 'PermanentAddressZip' },
      ]);
    },
    copyFields: function (keys) {
      keys.forEach(x => {
        this.answerSet.answers[this.getAnswerIndex(x.dstKey)].value = this.answerSet.answers[this.getAnswerIndex(x.srcKey)].value;
      });
    },
    uploadFile: function (questionKey, file) {
      let formData = new FormData();
      formData.append("file", file);
      axios
        .post('/api/answerSet/' + this.answerSet.id + '/file',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then(response => {
          this.answerSet.answers[this.getAnswerIndex(questionKey)].value = response.data;
        })
        .catch(error => {
          console.log(error);
        });
    },
    downloadFile: function (questionKey, filename) {
      axios({
        url: '/api/account/temporaryAccessToken',
        method: 'GET'
      }).then((response) => {
        window.open('/api/answerSet/' + this.answerSet.id + '/file/' + filename + '?temporaryAccessToken=' + response.data.token, '_blank');
        this.isDownloading = false;
      });
    },
    renderMarkdown: function (value) {
      return marked(value);
    },
    getPageUrl: function (page) {
      return '/client/form/' + this.formId + '/page/' + page.id;
    },
    getAnswerIndex: function (key) {
      return this.answerSet.answers.findIndex(a => a.key == key);
    },
    isVisible: function (question) {
      if (!question.conditionalFieldKey || !question.conditionalFieldValue)
        return true;

      let answer = this.answerSet.answers.find(x => x.key == question.conditionalFieldKey);
      if (!answer)
        return true;

      if (question.conditionalOperator == 'Equals')
        return answer.value == question.conditionalFieldValue;
      else if (question.conditionalOperator == 'NotEquals')
        return answer.value != question.conditionalFieldValue;
      else if (question.conditionalOperator == 'NotNullAndNotEquals')
        return answer.value && answer.value != question.conditionalFieldValue;
      else
        console.log('Unknown conditional operator: ' + question.conditionalOperator);

      return true;
    },
    load: function () {
      axios
        .get('/api/form/' + this.formId + '?includes=pages,pages.template,client')
        .then(response => {
          console.log(response.data);
          this.form = response.data;
          this.form.pages.sort((a, b) => a.displayWeight - b.displayWeight);

          this.loadAnswerSet();
        })
        .catch(error => {
          console.log(error);
        });
    },
    loadAnswerSet: function () {
      this.answerSet = null;
      if (this.page.displayWeight > this.maxDisplayWeight)
        this.maxDisplayWeight = this.page.displayWeight;

      axios
        .get('/api/answerSet/' + this.page.answerSetId)
        .then(response => {
          this.processAnswerSet(response.data);

          window.scrollTo(0, 0);
        })
        .catch(error => {
          console.log(error);
        });
    },
    processAnswerSet: function (answerSet) {
      this.answerSet = answerSet;

      this.answerSet.page = null;

      this.page.template.questions.forEach(q => {
        if (!this.answerSet.answers.find(a => a.key == q.key))
          this.answerSet.answers.push({ key: q.key, value: null });
      });
      this.savedAnswers = JSON.stringify(this.answerSet.answers);
    },
    saveNext: function () {
      let invalidFields = this.getInvalidFields();

      if (invalidFields.length == 0) {
        if (this.pageIndex < this.form.pages.length - 1)
          this.save(this.getPageUrl(this.form.pages[this.pageIndex + 1]));
        else
          this.save('/client/form/' + this.formId + '/review');
      } else {
        const h = this.$createElement;
        let items = invalidFields.map(x => h('li', {}, x));

        const invalidFieldsMessage = 
          h(
            'div',
            { },
            [
              'Please review the answers highlighted in red before continuing to the next page:',
              h(
                'ul',
                { },
                items
              )
            ]
          )
        this.$parent.showToast([invalidFieldsMessage], {
          title: 'Please Review Your Answers',
          variant: 'danger',
          autoHideDelay: 5000
        });
      }
    },
    save: function (nextUrl) {
      const submittedAnswers = JSON.stringify(this.answerSet.answers);
      return axios
        .put('/api/answerSet/' + this.page.answerSetId, this.answerSet)
        .then(response => {
          this.savedAnswers = submittedAnswers;

          if (nextUrl)
            this.$router.push(nextUrl);
          else {
            this.processAnswerSet(response.data);

            this.$parent.showToast(this.form.client.isOffline
              ? 'Saved on this device — use Check In to synchronize.'
              : 'Your changes have been saved.', {
              title: 'Saved',
              variant: 'success',
              autoHideDelay: 5000
            });
          }
          return true;
        })
        .catch(error => {
          console.log(error);

          this.$parent.showToast('An error occurred while saving your changes.', {
            title: 'Error Saving',
            variant: 'danger',
            autoHideDelay: 5000
          });
          return false;

        });
    }
  },
  beforeRouteLeave (to, from, next) {
    if (!this.hasUnsavedAnswers)
      return next();
    this.save().then(saved => next(saved));
  },
  beforeRouteUpdate (to, from, next) {
    if (!this.hasUnsavedAnswers)
      return next();
    this.save().then(saved => next(saved));
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.warnBeforeLeaving);
  },
  watch: {
    $route (to, from) {
      this.loadAnswerSet();
    }
  },
  created() {
  },
  async mounted () {
    window.addEventListener('beforeunload', this.warnBeforeLeaving);
    this.load();
  }
};
</script>